(() => {
  const rootSelector = '[data-desiros-customer-reviews]';
  const widgetPrimarySelector = '.amazon-reviews, .trustpilot-reviews';
  const widgetFallbackSelector = '.amazon-stars, .trustpilot-rating, .trustpilot-stars, .trustpilot-text';

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const moveToReviews = (root) => {
    const heading = root.querySelector('[data-desiros-reviews-heading]');
    if (!heading) return;
    const viewportTop = window.visualViewport?.offsetTop || 0;
    const isVisibleTopOverlay = (element) => {
      const style = getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      const positioned = style.position === 'fixed' || style.position === 'sticky';
      const visible = style.display !== 'none' && style.visibility !== 'hidden'
        && Number(style.opacity) !== 0 && rect.width > 0 && rect.height > 0;
      return positioned && visible ? rect : null;
    };
    const topChrome = Array.from(document.querySelectorAll('header, .shopify-section--announcement-bar'))
      .map(isVisibleTopOverlay)
      .filter(Boolean)
      .sort((a, b) => a.top - b.top);
    let baselineOffset = viewportTop;
    topChrome.forEach((rect) => {
      if (rect.top <= baselineOffset + 1 && rect.bottom > baselineOffset) baselineOffset = rect.bottom;
    });
    const stickyBars = Array.from(document.querySelectorAll('product-sticky-bar'))
      .map(isVisibleTopOverlay)
      .filter(Boolean);
    stickyBars.forEach((rect) => {
      if (rect.top <= baselineOffset + 1 && rect.bottom > baselineOffset) baselineOffset = rect.bottom;
    });
    heading.style.scrollMarginTop = `${Math.max(24, Math.ceil(baselineOffset - viewportTop + 24))}px`;
    heading.focus({ preventScroll: true });
    heading.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  };

  const bindWidgetHooks = (root) => {
    const mainProduct = document.querySelector('.shopify-section--main-product');
    if (!mainProduct) return;
    const primary = Array.from(mainProduct.querySelectorAll(widgetPrimarySelector));
    const candidates = primary.length > 0
      ? primary
      : Array.from(mainProduct.querySelectorAll(widgetFallbackSelector));
    const outermost = candidates.filter((candidate) => !candidates.some((other) => other !== candidate && other.contains(candidate)));

    outermost.forEach((widget) => {
      if (root.contains(widget)) return;
      const control = widget;
      if (control.dataset.desirosReviewsHook === 'true') return;
      control.dataset.desirosReviewsHook = 'true';
      control.setAttribute('aria-controls', root.id);

      const anchors = control.matches('a') ? [control] : Array.from(control.querySelectorAll('a'));
      anchors.forEach((anchor) => {
        anchor.dataset.desirosOriginalHref = anchor.getAttribute('href') || '';
        anchor.setAttribute('href', `#${root.id}`);
      });

      if (!control.matches('a, button') && !control.querySelector('a, button')) {
        control.setAttribute('role', 'link');
        control.tabIndex = 0;
      }
    });
  };

  const activateWidgetHook = (event) => {
    const control = event.target.closest?.('[data-desiros-reviews-hook="true"]');
    if (!control || !control.closest('.shopify-section--main-product')) return;
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    const root = document.querySelector(rootSelector);
    if (!root?.isConnected) return;
    event.preventDefault();
    event.stopPropagation();
    moveToReviews(root);
  };

  const init = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.desirosReviewsInitialized === 'true') return;
    root.dataset.desirosReviewsInitialized = 'true';

    const cards = Array.from(root.querySelectorAll('[data-desiros-review]'));
    const pageButtons = Array.from(root.querySelectorAll('[data-desiros-page]'));
    const previous = root.querySelector('[data-desiros-previous]');
    const next = root.querySelector('[data-desiros-next]');
    const status = root.querySelector('[data-desiros-page-status]');
    const dialog = root.querySelector('[data-desiros-dialog]');
    const dialogImage = root.querySelector('[data-desiros-dialog-image]');
    const dialogCaption = root.querySelector('[data-desiros-dialog-caption]');
    const dialogClose = root.querySelector('[data-desiros-dialog-close]');
    const summaryLink = root.querySelector('[data-desiros-summary-link]');
    const pageSize = 5;
    const totalPages = Math.ceil(cards.length / pageSize);
    let currentPage = 1;
    let activePhotoButton = null;

    const setPage = (page, shouldMove) => {
      currentPage = Math.min(Math.max(page, 1), totalPages);
      const firstReview = (currentPage - 1) * pageSize + 1;
      const lastReview = Math.min(currentPage * pageSize, cards.length);

      cards.forEach((card, index) => {
        card.hidden = Math.floor(index / pageSize) + 1 !== currentPage;
      });

      pageButtons.forEach((button) => {
        if (Number(button.dataset.desirosPage) === currentPage) button.setAttribute('aria-current', 'page');
        else button.removeAttribute('aria-current');
      });

      if (previous) previous.disabled = currentPage === 1;
      if (next) next.disabled = currentPage === totalPages;
      if (status) {
        status.textContent = root.dataset.pageStatus
          .replace('{page}', String(currentPage))
          .replace('{pages}', String(totalPages))
          .replace('{first}', String(firstReview))
          .replace('{last}', String(lastReview))
          .replace('{total}', String(cards.length));
      }
      if (shouldMove) moveToReviews(root);
    };

    pageButtons.forEach((button) => {
      button.addEventListener('click', () => setPage(Number(button.dataset.desirosPage), true));
    });
    previous?.addEventListener('click', () => setPage(currentPage - 1, true));
    next?.addEventListener('click', () => setPage(currentPage + 1, true));
    summaryLink?.addEventListener('click', (event) => {
      event.preventDefault();
      moveToReviews(root);
    });

    root.querySelectorAll('[data-desiros-photo]').forEach((button) => {
      button.addEventListener('click', () => {
        const thumbnail = button.querySelector('img');
        const caption = button.closest('figure')?.querySelector('figcaption')?.textContent || '';
        if (!thumbnail || !dialog || !dialogImage || !dialogCaption) return;
        activePhotoButton = button;
        dialogImage.src = thumbnail.currentSrc || thumbnail.src;
        dialogImage.alt = caption;
        dialogCaption.textContent = caption;
        dialog.showModal();
      });
    });

    dialogClose?.addEventListener('click', () => dialog?.close());
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog?.addEventListener('close', () => {
      dialogImage?.removeAttribute('src');
      activePhotoButton?.focus();
    });

    root.classList.add('desiros-cr--enhanced');
    setPage(1, false);
    bindWidgetHooks(root);
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(rootSelector)) init(scope);
    scope.querySelectorAll?.(rootSelector).forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  if (!window.__desirosCustomerReviewsDelegatedHooks) {
    window.__desirosCustomerReviewsDelegatedHooks = true;
    document.addEventListener('click', activateWidgetHook, true);
    document.addEventListener('keydown', activateWidgetHook, true);
    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
      document.querySelectorAll(rootSelector).forEach(bindWidgetHooks);
    });
  }
})();
